import type { MouseEvent } from 'react';

import { Combobox } from '@base-ui/react/combobox';
import styled, { css } from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

const SELECT_ZINDEX = 99999;

export const Control = styled(Combobox.InputGroup)<{
  $hasError: boolean;
  $isDisabled: boolean;
  $isSearchable?: boolean;
}>((p) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  // min-height, not height: SelectMulti's chips wrap onto extra rows and the
  // control must grow with them instead of letting them spill past its edges.
  minHeight: theme.base.height,
  borderWidth: theme.form.border.width,
  borderStyle: 'solid',
  borderColor: p.$hasError ? theme.color.elementRed100 : theme.form.border.color,
  borderRadius: theme.form.border.radius,
  backgroundColor: p.$hasError ? theme.color.redTrans5 : theme.form.bg.color,
  font: theme.font.regular16,
  color: theme.form.value.color,
  transition: transitionStandard('border-color', 'background-color'),
  cursor: p.$isDisabled ? 'not-allowed' : p.$isSearchable === false ? 'pointer' : 'text',
  ...(p.$isDisabled && { opacity: 0.7 }),
  // base-ui only emits [data-focused] under a Field.Root, which this select
  // doesn't use — :focus-within tracks the input's real focus instead, so the
  // control stays highlighted after a pick closes the popup.
  '&[data-popup-open], &:focus-within': {
    borderColor: p.$hasError ? theme.color.elementRed100 : theme.form.border.colorFocus,
    backgroundColor: p.$hasError ? theme.color.redTrans5 : theme.form.bg.colorFocus,
  },
  '&:hover:not([data-popup-open]):not(:focus-within):not([data-disabled])': {
    backgroundColor: p.$hasError ? theme.color.redTrans5 : theme.form.bg.colorHover,
  },
}));

// A bare <input> contributes the browser-default ~20-character width to
// layout, inflating any shrink-wrapped control (e.g. PhoneField's country
// picker) past its content. The sizer autosizes instead: a hidden ::after
// mirrors the input's visible text (via data-value) in the same grid cell, and
// the ValueOverlay shares that cell, so the cell — and the control's intrinsic
// width — tracks whatever is showing, while the input (width 100%, size=1)
// just fills the cell.
export const InputSizer = styled.div<{ $hasPrefix?: boolean }>((p) => ({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'inline-grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  overflow: 'hidden',
  marginLeft: p.$hasPrefix ? 0 : pxToRem(8),
  padding: `0 ${pxToRem(4)} 0 ${p.$hasPrefix ? 0 : pxToRem(4)}`,
  '&::after': {
    content: 'attr(data-value) " "',
    visibility: 'hidden',
    whiteSpace: 'pre',
    gridArea: '1 / 1',
    font: theme.font.regular16,
  },
}));

export const inputPlaceholderStyle = {
  '&::placeholder': {
    color: theme.form.placeholder.color,
  },
  '&:focus::placeholder': {
    color: theme.form.placeholder.colorFocus,
  },
};

export const Input = styled(Combobox.Input).attrs({ size: 1 })<{
  $isValueHidden?: boolean;
  $isCaretHidden?: boolean;
}>((p) => ({
  gridArea: '1 / 1',
  width: '100%',
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  padding: 0,
  font: theme.font.regular16,
  // While the ValueOverlay renders the selection on top of the input, the
  // input's echoed text must not shine through underneath.
  color: p.$isValueHidden ? 'transparent' : theme.form.value.color,
  caretColor: p.$isCaretHidden ? 'transparent' : theme.form.value.color,
  textOverflow: 'ellipsis',
  cursor: 'inherit',
  ...inputPlaceholderStyle,
}));

export const PrefixContainer = styled.span({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  flexShrink: 1,
  overflow: 'hidden',
  paddingLeft: pxToRem(8),
});

export const Prefix = styled.span({
  color: theme.color.textHelper,
  marginRight: pxToRem(4),
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Shares the InputSizer's grid cell with the input, so it sizes the cell in a
// shrink-wrapped control and truncates with it in a fixed-width one. A block,
// not a flex container, so text-overflow applies to a string label.
export const ValueOverlay = styled.div({
  gridArea: '1 / 1',
  alignSelf: 'center',
  minWidth: 0,
  pointerEvents: 'none',
  // A tooltip trigger inside the label needs the pointer to show its tooltip;
  // a press on it still bubbles to the input group, which opens the list.
  '& [aria-describedby]': { pointerEvents: 'auto' },
  font: theme.font.regular16,
  color: theme.form.value.color,
  '--icon-color': theme.color.elementPurple80,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // The overlay's own ellipsis only reaches a string label. A rich label sets
  // its text in boxes of its own (Text wraps by default, a Stack makes them
  // flex items), so every box stays on one line and each box holding only text
  // truncates itself; a box without text, like an image, keeps its size. The
  // first text box is the label and gives up width first, so what follows it,
  // like a dial code or a status icon, stays whole. Doubled to outrank Text's
  // own white-space.
  '&& *': { whiteSpace: 'nowrap' },
  '&& :not(:has(*)):not(:empty)': { overflow: 'hidden', textOverflow: 'ellipsis' },
  '&& :not(:has(*)):not(:empty) ~ *': { flexShrink: 0 },
});

export const ControlAnchor = styled.div({
  width: '100%',
});

export const Indicators = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(4),
  flexShrink: 0,
  marginRight: pxToRem(8),
  cursor: 'pointer',
  [`${Control}[data-disabled] &`]: {
    cursor: 'not-allowed',
  },
});

const indicatorButtonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  color: theme.form.icon.color,
  '&:hover': {
    color: theme.form.icon.colorHover,
  },
  '&:focus-visible': {
    outline: 'none',
  },
});

export const ClearButton = styled(Combobox.Clear)`
  ${indicatorButtonStyle};
`;

export const DropdownButton = styled.span({
  display: 'inline-flex',
  alignItems: 'center',
  color: theme.form.icon.color,
  pointerEvents: 'none',
  [`${Control}:not([data-disabled]) ${Indicators}:hover &`]: {
    color: theme.form.icon.colorHover,
  },
});

// Marks what a select portals to the body, so outside-click handlers (a
// closeOnClickOutside sidebar) can tell a press on an open select apart from
// one outside it.
export const SELECT_PORTAL_ATTR = 'data-select-portal';

const selectPortalAttrs = { [SELECT_PORTAL_ATTR]: '' };

// Swallows the click that dismisses the popup, so clicking outside an open
// select closes it without activating whatever sits underneath.
export const Backdrop = styled(Combobox.Backdrop).attrs(selectPortalAttrs)({
  position: 'fixed',
  inset: 0,
  zIndex: SELECT_ZINDEX,
});

// A react-aria modal (InlineSidebar) aria-hides every node added to the body
// outside it, the portal included, unless the node is marked as a top layer.
const reactAriaTopLayerAttrs = { 'data-react-aria-top-layer': 'true' };

export const Portal = styled(Combobox.Portal).attrs(reactAriaTopLayerAttrs)``;

export const WIDE_POPUP_WIDTH = pxToRem(320);

export const Positioner = styled(Combobox.Positioner).attrs(selectPortalAttrs)<{
  $minWidthRem?: string;
}>((p) => ({
  zIndex: SELECT_ZINDEX,
  width: p.$minWidthRem
    ? `min(max(var(--anchor-width), ${p.$minWidthRem}), var(--available-width))`
    : 'var(--anchor-width)',
  maxWidth: 'var(--available-width)',
}));

// A mouse press on the popup's padding or empty message would otherwise move
// focus off the input and blur it while the user is still picking.
const keepInputFocused = (event: MouseEvent<HTMLElement>) => {
  if (event.button === 0) event.preventDefault();
};

export const Popup = styled(Combobox.Popup).attrs({
  onMouseDown: keepInputFocused,
})<{ $isHiddenWhenEmpty?: boolean }>((p) => ({
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(8),
  boxShadow: theme.shadow.high,
  outline: 'none',
  // No max-height: the scroll area inside (List, or Scroller when virtualized)
  // caps the height, so a trailing addOption row never gets clipped.
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  ...(p.$isHiddenWhenEmpty && { '&[data-empty]': { display: 'none' } }),
}));

export const LIST_PADDING = 16;

export const List = styled(Combobox.List)({
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  maxHeight: 'min(20rem, var(--available-height))',
  overscrollBehavior: 'contain',
  padding: pxToRem(LIST_PADDING),
  minHeight: 0,
});

export const VirtualList = styled(Combobox.List)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  padding: 0,
});

export const Scroller = styled.div({
  // Explicit height (seeded from the virtualizer's total size via --total-size)
  // rather than a flex constraint, so the virtualizer always measures a bounded
  // viewport and renders only the visible rows instead of all of them.
  height: 'min(20rem, var(--total-size))',
  maxHeight: 'var(--available-height)',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingLeft: pxToRem(LIST_PADDING),
  paddingRight: pxToRem(LIST_PADDING),
});

export const VirtualRows = styled.div({
  position: 'relative',
  width: '100%',
});

export const Item = styled(Combobox.Item)(() => ({
  display: 'flex',
  alignItems: 'stretch',
  flexShrink: 0,
  minHeight: theme.base.height,
  padding: 0,
  borderRadius: theme.form.border.radius,
  font: theme.font.regular14,
  color: theme.form.option.color,
  '--icon-color': theme.color.elementPurple80,
  backgroundColor: theme.form.option.bg.color,
  cursor: 'pointer',
  outline: 'none',
  '&[data-highlighted]': {
    backgroundColor: theme.form.option.bg.colorFocus,
    color: theme.form.option.colorHover,
  },
  '&[data-disabled]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export const OptionContainer = styled.div<{ $helpText?: string | null }>((p) => ({
  padding: p.$helpText ? pxToRem(12) : `${pxToRem(10)} ${pxToRem(12)}`,
  font: p.$helpText ? theme.font.semibold14 : theme.font.regular14,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  minWidth: 0,
}));

export const HelpText = styled.div({
  font: theme.font.regular14,
  color: theme.color.textHelper,
  textWrap: 'wrap',
});

export const FocusIconContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingRight: pxToRem(12),
});

export const ReturnHint = styled.span.attrs({ 'data-testid': 'select-return-hint' })({
  display: 'inline-flex',
  alignItems: 'center',
  '--icon-color': theme.form.icon.color,
  // Hidden by visibility rather than left unrendered so the reserved width
  // keeps wrapped labels from reflowing as the highlight moves between rows.
  visibility: 'hidden',
  '[data-highlighted] &': {
    visibility: 'visible',
  },
});

// Option labels never truncate: an ellipsis hides the text and its hover
// tooltip is unreachable by keyboard or touch. Long labels wrap instead.
export const OptionLabel = styled.div({
  overflowWrap: 'break-word',
});

export const EmptyMessage = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  overflowWrap: 'anywhere',
  minHeight: theme.base.height,
  margin: pxToRem(16),
  font: theme.font.regular14,
  color: theme.color.textHelper,
});

// Sits below the Scroller rather than inside it, so it re-applies the list's
// inset itself.
export const AddOptionLink = styled(Combobox.Item)({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  minHeight: theme.base.height,
  margin: `0 ${pxToRem(LIST_PADDING)} ${pxToRem(LIST_PADDING)}`,
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: theme.form.border.radius,
  font: theme.font.regular14,
  color: theme.color.textPrimary,
  textDecoration: 'none',
  cursor: 'pointer',
  outline: 'none',
  '&[data-highlighted]': {
    backgroundColor: theme.form.option.bg.colorFocus,
  },
});
